import React from 'react';
import Icon from './ui-stubs';
import { Button } from './ui-stubs';
import { Checkbox } from './ui-stubs';
import { Select } from './ui-stubs';

const FilterPanel = ({ isOpen, onToggle, filters, onFiltersChange, onClearFilters, tasks }) => {
  // Mock current user ID (in real app, this would come from auth context)
  const CURRENT_USER_ID = 'u1';

  // Extract unique values from tasks for filter options
  const uniqueStatuses = [...new Set(tasks?.map(t => t?.status))];
  const uniquePriorities = ['Critical', 'High', 'Medium', 'Low'];
  const uniqueTags = [...new Set(tasks?.flatMap(t => t?.tags))];
  const uniqueTaskTypes = [...new Set(tasks?.map(t => t?.taskType))];

  // Extract unique owners from tasks
  const uniqueOwners = Array.from(
    new Map(tasks?.map(t => t?.owner).filter(Boolean).map(owner => [owner?.id, owner]))?.values()
  );

  // Extract unique contributors from tasks
  const uniqueContributors = Array.from(
    new Map(
      tasks?.flatMap(t => t?.contributors || []).filter(Boolean).map(contributor => [contributor?.id, contributor])
    )?.values()
  );

  // Extract unique checklist assignees from tasks
  const uniqueChecklistAssignees = Array.from(
    new Map(
      tasks?.flatMap(t => t?.checklist || [])
        .map(item => item?.assignee)
        .filter(Boolean)
        .map(assignee => [assignee?.id, assignee])
    )?.values()
  );

  const handleFilterChange = (key, value) => {
    onFiltersChange(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleMultiSelectChange = (key, value) => {
    onFiltersChange(prev => {
      const currentValues = prev?.[key] || [];
      const newValues = currentValues?.includes(value)
        ? currentValues?.filter(v => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [key]: newValues
      };
    });
  };

  const handleMyWorkToggle = () => {
    onFiltersChange(prev => ({
      ...prev,
      myWork: !prev?.myWork
    }));
  };

  const activeFilterCount = Object.entries(filters)?.reduce((count, [key, value]) => {
    if (key === 'myWork' && value) return count + 1;
    if (Array.isArray(value)) return count + value?.length;
    if (typeof value === 'object' && value !== null) {
      return count + (value?.start ? 1 : 0) + (value?.end ? 1 : 0);
    }
    return count + (value && value !== 'all' && value !== '' ? 1 : 0);
  }, 0);

  if (!isOpen) {
    return (
      <div className="fixed left-0 top-[140px] z-30">
        <button
          onClick={onToggle}
          className="bg-card border border-border rounded-r-lg p-3 shadow-md hover:bg-gray-50 transition-colors"
        >
          <Icon name="SlidersHorizontal" size={20} className="text-gray-600" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-medium w-5 h-5 rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-r border-border h-[calc(100vh-140px)] overflow-y-auto sticky top-[140px]">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-card pb-2 border-b border-border z-10">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            {activeFilterCount > 0 && (
              <span className="bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={onToggle}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Clear All Button */}
        {activeFilterCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="w-full mb-4"
            iconName="X"
          >
            Clear All Filters
          </Button>
        )}

        {/* My Work Quick Filter */}
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Checkbox
            id="my-work"
            checked={filters?.myWork}
            onChange={handleMyWorkToggle}
            label={
              <div className="flex items-center space-x-2">
                <Icon name="User" size={16} className="text-blue-600" />
                <span className="font-semibold text-gray-900">My Work</span>
              </div>
            }
          />
          <p className="text-xs text-gray-600 mt-1 ml-6">
            Show tasks where I'm the owner, contributor, or assigned to checklist items
          </p>
        </div>

        {/* Live Tasks Quick Filter */}
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Checkbox
            id="live-tasks"
            checked={filters?.liveTasks}
            onChange={() => handleFilterChange('liveTasks', !filters?.liveTasks)}
            label={
              <div className="flex items-center space-x-2">
                <Icon name="Activity" size={16} className="text-green-600" />
                <span className="font-semibold text-gray-900">Live tasks</span>
              </div>
            }
          />
          <p className="text-xs text-gray-600 mt-1 ml-6">
            Show tasks that are actively being worked on right now.
          </p>
        </div>

        {/* Filter Sections */}
        <div className="space-y-6">
          {/* RESPONSIBILITY SECTION */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Users" size={16} className="text-gray-500" />
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Responsibility</h3>
            </div>
            <div className="space-y-4 pl-1">
              {/* Task Owner Filter */}
              {uniqueOwners?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Task Owner</h4>
                  <p className="text-xs text-gray-500 mb-2">Accountable person for task outcome</p>
                  <div className="space-y-2">
                    {uniqueOwners?.map((owner) => (
                      <div key={owner?.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`owner-${owner?.id}`}
                          checked={filters?.ownerIds?.includes(owner?.id)}
                          onChange={() => handleMultiSelectChange('ownerIds', owner?.id)}
                        />
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold">
                            {owner?.avatar}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700">{owner?.name}</span>
                            {owner?.role && (
                              <span className="text-xs text-gray-500">{owner?.role}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contributors Filter */}
              {uniqueContributors?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Contributors</h4>
                  <p className="text-xs text-gray-500 mb-2">Collaborators on the task</p>
                  <div className="space-y-2">
                    {uniqueContributors?.map((contributor) => (
                      <div key={contributor?.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`contributor-${contributor?.id}`}
                          checked={filters?.contributorIds?.includes(contributor?.id)}
                          onChange={() => handleMultiSelectChange('contributorIds', contributor?.id)}
                        />
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                            {contributor?.avatar}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700">{contributor?.name}</span>
                            {contributor?.role && (
                              <span className="text-xs text-gray-500">{contributor?.role}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Checklist Assigned To Filter */}
              {uniqueChecklistAssignees?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Checklist Assigned To</h4>
                  <p className="text-xs text-gray-500 mb-2">Responsible for specific checklist items</p>
                  <div className="space-y-2">
                    {uniqueChecklistAssignees?.map((assignee) => (
                      <div key={assignee?.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`checklist-assignee-${assignee?.id}`}
                          checked={filters?.checklistAssigneeIds?.includes(assignee?.id)}
                          onChange={() => handleMultiSelectChange('checklistAssigneeIds', assignee?.id)}
                        />
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                            {assignee?.avatar}
                          </div>
                          <span className="text-sm text-gray-700">{assignee?.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* STATE SECTION */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Activity" size={16} className="text-gray-500" />
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">State</h3>
            </div>
            <div className="space-y-4 pl-1">
              {/* Status Filter */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Status</h4>
                <div className="space-y-2">
                  {uniqueStatuses?.map((status) => (
                    <Checkbox
                      key={status}
                      id={`status-${status}`}
                      checked={filters?.status?.includes(status)}
                      onChange={() => handleMultiSelectChange('status', status)}
                      label={status}
                    />
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Priority</h4>
                <div className="space-y-2">
                  {uniquePriorities?.map((priority) => (
                    <Checkbox
                      key={priority}
                      id={`priority-${priority}`}
                      checked={filters?.priorities?.includes(priority)}
                      onChange={() => handleMultiSelectChange('priorities', priority)}
                      label={priority}
                    />
                  ))}
                </div>
              </div>

              {/* Task Type Filter */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Task Type</h4>
                <Select
                  value={filters?.taskType}
                  onChange={(value) => handleFilterChange('taskType', value)}
                  options={[
                    { value: '', label: 'All Types' },
                    ...uniqueTaskTypes?.map(type => ({ value: type, label: type }))
                  ]}
                  placeholder="Select type"
                />
              </div>
            </div>
          </div>

          {/* TIME SECTION */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Calendar" size={16} className="text-gray-500" />
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Time</h3>
            </div>
            <div className="space-y-4 pl-1">
              {/* Due Date Range */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Due Date Range</h4>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={filters?.dateRange?.start || ''}
                    onChange={(e) => handleFilterChange('dateRange', { ...filters?.dateRange, start: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Start date"
                  />
                  <input
                    type="date"
                    value={filters?.dateRange?.end || ''}
                    onChange={(e) => handleFilterChange('dateRange', { ...filters?.dateRange, end: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="End date"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* STRUCTURE SECTION */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Network" size={16} className="text-gray-500" />
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Structure</h3>
            </div>
            <div className="space-y-4 pl-1">
              {/* Tags Filter */}
              {uniqueTags?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Tags</h4>
                  <div className="space-y-2">
                    {uniqueTags?.map((tag) => (
                      <Checkbox
                        key={tag}
                        id={`tag-${tag}`}
                        checked={filters?.tags?.includes(tag)}
                        onChange={() => handleMultiSelectChange('tags', tag)}
                        label={tag}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Has Dependencies Filter */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Has Dependencies</h4>
                <Select
                  value={filters?.hasDependencies}
                  onChange={(value) => handleFilterChange('hasDependencies', value)}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  placeholder="Select option"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;